<script setup>
import { computed, ref, inject, onMounted, getCurrentInstance, nextTick } from 'vue'
import { useElementBounding } from '@vueuse/core'

import BaseIcon from '@dogfy/components/base/BaseIcon.vue'

import { toTitleCase, scrollIntoElementAndFocus } from '@dogfy/lib/utils'
import customRegex from '@dogfy/lib/regex'

const [model, modifiers] = defineModel({
  type: [String, Number],
  set (newValue) {
    let value = newValue

    if (isNumber.value) {
      if (props.maxDecimals !== null) {
        const [integer, decimal] = value.toString().split(/[.,]/)
        if (decimal?.length > props.maxDecimals) {
          value = Number(value.toString().slice(0, integer.length + 1 + props.maxDecimals))
        }
      }

      if (props.maxLength !== null && value.toString().replace(/[.,]/g, '').length > props.maxLength) {
        const length = value.toString().match(/[.,]/) ? props.maxLength + 1 : props.maxLength
        value = Number(value.toString().slice(0, length))
      }

      // Force input value to show the current v-model value.
      // Used to avoid the use of value attribute on input as this brings an erratic behaviour with input[type=number].
      instance?.proxy?.$forceUpdate()
    }

    if (modifiers.sanitize) {
      // Double check to avoid triggering the trim modifier (if present).
      if (customRegex.nonAlphanumericLatinWithSymbols.test(value)) {
        value = value.replace(customRegex.nonAlphanumericLatinWithSymbols, '')
        instance?.proxy?.$forceUpdate()
      }
    }

    if (modifiers.capitalize) {
      value = value.charAt(0).toUpperCase() + value.slice(1)
    }

    if (modifiers.lowercase) {
      value = value.toLowerCase()
    }

    if (modifiers.uppercase) {
      value = value.toUpperCase()
    }

    if (modifiers.titlecase) {
      value = toTitleCase(value)
    }

    if (modifiers.numbers) {
      // Remove non-digits values.
      value = value?.toString()?.replace(/\D/g, '') || ''
      instance?.proxy?.$forceUpdate()
    }

    return value
  },
})

const props = defineProps({
  type: {
    type: String,
    default: 'text',
  },
  placeholder: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: null,
  },
  units: {
    type: String,
    default: null,
  },
  label: {
    type: String,
    default: null,
  },
  id: {
    type: String,
    default: null,
  },
  autocomplete: {
    type: String,
    default: null,
  },
  inputmode: {
    type: String,
    default: null,
  },
  min: {
    type: Number,
    default: null,
  },
  max: {
    type: Number,
    default: null,
  },
  maxLength: {
    type: Number,
    default: null,
  },
  maxDecimals: {
    type: Number,
    default: null,
  },
  step: {
    type: Number,
    default: null,
  },
  hasIconRight: {
    type: Boolean,
    default: false,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  isReadonly: {
    type: Boolean,
    default: false,
  },
  isFullwidth: {
    type: Boolean,
    default: () => inject('isFullwidth', false),
  },
  isFitted: {
    type: Boolean,
    default: false,
  },
  hasPasswordReveal: {
    type: Boolean,
    default: false,
  },
  hasCenteredText: {
    type: Boolean,
    default: false,
  },
  requestFocus: {
    type: Boolean,
    default: false,
  },
  isAltStyle: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['blur', 'focus', 'keyup', 'icon-click', 'icon-release'])

const injectedFieldType = inject('fieldType', '')

const statusTypeIcons = {
  success: 'check-circle',
  warning: 'exclamation',
  error: 'exclamation-circle',
}

const fieldInputRef = ref(null)

const isMouseoverIcon = ref(false)

const isPasswordRevealed = ref(false)

const labelRef = ref(null)
const { width: labelWidth } = useElementBounding(labelRef)

const baseFieldInputRef = ref(null)
const dataTestId = ref(null)

const instance = getCurrentInstance()

const isFullwidth = computed(() => props.isFullwidth && !props.isFitted)

const fieldType = computed(() => Object.keys(statusTypeIcons).includes(injectedFieldType.value) ? injectedFieldType.value : null)

const statusTypeIcon = computed(() => statusTypeIcons[fieldType.value])

const inputSize = computed(() => props.isFitted ? model.value?.toString().length || 1 : null)

const inputType = computed(() => {
  let type = props.type

  if (props.hasPasswordReveal && props.type === 'password') {
    type = isPasswordRevealed.value ? 'text' : 'password'
  }

  return type
})

const icon = computed(() => {
  let icon = props.icon

  if (props.hasPasswordReveal && props.type === 'password') {
    icon = isPasswordRevealed.value ? 'eye-slash' : 'eye'
  }

  return icon
})

const isIconClickable = computed(() => props.hasPasswordReveal && props.type === 'password')

const isIconRight = computed(() => props.hasIconRight || (props.hasPasswordReveal && props.type === 'password'))

const canHaveIconRight = computed(() => !!icon.value && !!isIconRight.value)

const canHaveIconLeft = computed(() => !!icon.value && !isIconRight.value)

const hasValue = computed(() => model.value?.length > 0)

const isNumber = computed(() => props.type === 'number')

const inputMinWidth = computed(() => props.placeholder?.length || 15)

function toggleMouseoverIcon () {
  isMouseoverIcon.value = !isMouseoverIcon.value
}

function manageIconClick () {
  if (props.hasPasswordReveal) {
    isPasswordRevealed.value = !isPasswordRevealed.value
    focusFieldInput()
  }
}

function focusFieldInput () {
  scrollIntoElementAndFocus(fieldInputRef.value)
}

onMounted(() => {
  if (props.requestFocus) {
    focusFieldInput()
  }

  nextTick(() => {
    dataTestId.value = baseFieldInputRef.value?.dataset.testId
    delete baseFieldInputRef.value?.dataset.testId
  })
})

defineExpose({ focusFieldInput })
</script>

<template>
  <div ref="baseFieldInputRef" class="base-field-input" :class="{ 'base-field-input--fullwidth': isFullwidth }">
    <BaseIcon
      v-if="icon"
      :key="icon"
      class="base-field-input__icon"
      :class="{
        [`base-field-input__icon--${fieldType}`]: isIconRight && fieldType,
        'base-field-input__icon--right': isIconRight,
        'base-field-input__icon--clickable': isIconClickable,
      }"
      :icon="icon"
      @mouseenter="toggleMouseoverIcon"
      @mouseleave="toggleMouseoverIcon"
      @click="manageIconClick"
      @pointerdown.prevent
    />
    <input
      :id="props.id"
      ref="fieldInputRef"
      v-model="model"
      :data-test-id="dataTestId"
      class="input base-field-input__input"
      :class="[
        fieldType ? `base-field-input__input--${fieldType}` : '',
        {
          'input--hovered': isMouseoverIcon,
          'base-field-input__input--has-icon-left': canHaveIconLeft,
          'base-field-input__input--has-icon-right': canHaveIconRight,
          'base-field-input__input--has-units': !!props.units && isNumber,
          'base-field-input__input--fullwidth': props.isFullwidth,
          'base-field-input__input--fitted': props.isFitted,
          'base-field-input__input--text-centered': props.hasCenteredText,
          'base-field-input__input--animated-label': props.label,
          'base-field-input__input--alt-style': props.isAltStyle,
        }
      ]"
      :type="inputType"
      :placeholder="props.placeholder"
      :size="inputSize"
      :disabled="props.isDisabled"
      :readonly="props.isReadonly"
      :max="props.max"
      :maxlength="props.maxLength"
      :min="props.min"
      :step="props.step"
      :autocomplete="props.autocomplete"
      :inputmode="props.inputmode"
      @focus="emit('focus')"
      @blur="emit('blur')"
      @keyup="emit('keyup', $event)"
    >
    <label
      v-if="props.label"
      ref="labelRef"
      for="input"
      class="base-field-input__placeholder"
    >
      {{ props.label }}
    </label>
    <p
      v-if="!!props.units && isNumber"
      class="base-field-input__units"
      :class="{
        'base-field-input__units--disabled': !hasValue,
        'base-field-input__units--alt-style': props.isAltStyle,
      }"
    >
      {{ props.units }}
    </p>
    <BaseIcon
      v-if="!!statusTypeIcon && !isIconRight"
      class="base-field-input__icon base-field-input__icon--right"
      :class="`base-field-input__icon--${fieldType}`"
      :icon="statusTypeIcon"
      @click="focusFieldInput()"
    />
  </div>
</template>

<style lang="scss">
.base-field-input {
  --max-length: v-bind('props.maxLength');

  $parent: &;

  $min-chars: 20ch;
  position: relative;

  display: flex;

  align-items: center;

  &:has(#{$parent}__icon--right) {
    #{$parent}__units {
      left: auto;
      right: $spacer*3;
    }
  }

  &:has(#{$parent}__input--alt-style) {
    #{$parent}__units {
      bottom: $spacer-half;
    }
  }

  &:has(#{$parent}__input--animated-label) {
    &:has(#{$parent}__input:focus),
    &:has(#{$parent}__input:not(:placeholder-shown)),
    &:has(#{$parent}__input:autofill),
    &:has(#{$parent}__input:-webkit-autofill) {
      #{$parent}__units {
        bottom: $spacer-half;

        transition: $transition-duration-medium all;
      }
    }
  }

  &--fullwidth {
    width: 100%;
  }

  &__placeholder {
    position: absolute;
    z-index: z-number(base-field-hover);
    top: $spacer;
    left: $spacer;

    transition: $transition-duration-medium all;

    font-size: ms(0);
    line-height: $font-lineheight-large;
    font-weight: $font-weight-regular;
    color: $color-neutral-dark;

    pointer-events: none;
    white-space: nowrap;
  }

  &__input {
    min-width: $min-chars; // Use the default input html element width. This is necessary to maintain the width when icon right is set.

    &--animated-label {
      &:not(:placeholder-shown),
      &:focus,
      &:autofill,
      &:-webkit-autofill {
        padding-top: $spacer*1.5;
        padding-bottom: $spacer-half;

        + #{$parent}__placeholder {
          z-index: z-number(base-field-hover);
          top: $spacer-half;
          left: $spacer;

          font-size: ms(-2);
          text-transform: uppercase;
        }
      }
    }

    &--fullwidth {
      width: 100% !important;
      min-width: unset;
    }

    &--fitted {
      min-width: auto;
      max-width: max-content;

      &[type=number],
      &#{$parent}__input--animated-label {
        box-sizing: unset;
        min-width: max(calc(v-bind(labelWidth) * 1px), calc(v-bind(inputSize) * 1ch));
      }

      &#{$parent}__input--alt-style {
        &:not([type='number']) {
          padding: 0;
          min-width: calc(v-bind(inputMinWidth) * 1ch);
          max-width: calc(var(--max-length, 15) * 1ch);
        }

        &::placeholder {
          color: $color-neutral-semidark;
        }
      }
    }

    &--success {
      border-color: $color-feedback-success-dark !important;
    }

    &--warning {
      border-color: $color-feedback-warning-dark !important;
    }

    &--error {
      border-color: $color-feedback-error-dark !important;
    }

    &--has-icon-left {
      padding-left: $spacer*3;
      width: calc($spacer*3 + $min-chars); // This is necessary to maintain the original shown chars.

      ~ #{$parent}__placeholder {
        left: $spacer*3 !important;
      }
    }

    &--has-icon-right {
      padding-right: $spacer*3;

      &#{$parent}__input--has-units {
        padding-right: $spacer*4.5; // Add enough space to have the icon and the units.
      }
    }

    &--text-centered {
      padding: 0;

      font-size: ms(4);
      text-align: center;
    }

    &--has-units {
      padding-right: $spacer*2.5;
    }

    &--alt-style {
      border: none;
      border-radius: 0;
      padding-top: 0;

      background: url('@dogfy/assets/icons/custom/dashed-line.svg') repeat-x bottom / 24px;

      font-size: ms(1, true);
      font-weight: $font-weight-light;

      @include breakpoint(lg) {
        font-size: ms(4, true);
        font-family: $font-family-highlight;
        font-weight: $font-weight-regular;
      }

      &:focus {
        box-shadow: unset;
      }
    }
  }

  &__icon {
    position: absolute;
    z-index: z-number(base-field-icon);
    top: auto;
    bottom: auto;
    left: $spacer;

    color: $color-neutral-dark;

    pointer-events: none;

    &--clickable {
      pointer-events: auto;

      &:hover {
        cursor: pointer;

        color: $color-neutral-darkest;
      }
    }

    &--right {
      left: auto;
      right: $spacer;
    }

    &--success {
      color: $color-feedback-success-dark;
    }

    &--warning {
      color: $color-feedback-warning-dark;
    }

    &--error {
      color: $color-feedback-error-dark;
    }
  }

  &__units {
    position: absolute;
    bottom: $spacer;
    left: auto;
    right: $spacer;

    line-height: $font-lineheight-large;

    &--disabled {
      color: $color-neutral-dark;
    }

    &--alt-style {
      font-weight: $font-weight-light;
      color: $color-neutral-semidark;

      @include breakpoint(lg) {
        font-weight: $font-weight-regular;
      }
    }
  }
}
</style>
